<script>
    /**
     * The feedback rating circles (as in LatteFeedbackActionsRatings), as a radio group. "none" is the neutral
     * grey circle for a plain note.
     */
    export const RATING_CIRCLE_BG_CLASSES = {
        none: 'bg-zinc-400',
        red: 'bg-lifeworx-red-500',
        yellow: 'bg-lifeworx-yellow-500',
        green: 'bg-lifeworx-green-500',
    }

    /**
     * The selected circle's ring, in the circle's own colour and offset from it.
     */
    export const RATING_CIRCLE_RING_CLASSES = {
        none: 'ring-2 ring-offset-2 ring-zinc-400 ring-offset-white',
        red: 'ring-2 ring-offset-2 ring-lifeworx-red-500 ring-offset-white',
        yellow: 'ring-2 ring-offset-2 ring-lifeworx-yellow-500 ring-offset-white',
        green: 'ring-2 ring-offset-2 ring-lifeworx-green-500 ring-offset-white',
    }

    /**
     * The composer border while a feedback colour is picked.
     */
    export const RATING_BORDER_CLASSES = {
        red: 'border-lifeworx-red-500',
        yellow: 'border-lifeworx-yellow-500',
        green: 'border-lifeworx-green-500',
    }

    /**
     * The same rating colours as text that stays readable on white and dark backgrounds (yellow-500 is too light
     * for text on white, so text uses a darker shade).
     */
    export const RATING_TEXT_CLASSES = {
        red: 'text-lifeworx-red-500',
        yellow: 'text-lifeworx-yellow-800',
        green: 'text-lifeworx-green-600',
    }

    export const RATING_CIRCLE_LABELS = {
        none: 'Plain note',
        red: 'Red rating',
        yellow: 'Yellow rating',
        green: 'Green rating',
    }

    export const FEEDBACK_RATINGS = ['red', 'yellow', 'green']

    export const COMPOSER_RATINGS = ['none', ...FEEDBACK_RATINGS]

    export function ratingCircleOpacity(selected, disabled) {
        if (disabled) {
            return 'opacity-25'
        }

        return selected ? 'opacity-100' : 'opacity-60 hover:opacity-100'
    }
</script>

<script setup>
    import { computed } from 'vue'

    const props = defineProps({
        modelValue: {
            type: String,
            default: null,
        },
        ratings: {
            type: Array,
            default: () => COMPOSER_RATINGS,
        },
        disabledRatings: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Labels the grey circle "Note" and the coloured ones "Feedback", with a separator between the two groups.
         */
        showGroupLabels: {
            type: Boolean,
            default: false,
        },
    })

    const groups = computed(() =>
        [
            { label: 'Note', ratings: props.ratings.filter((rating) => !FEEDBACK_RATINGS.includes(rating)) },
            { label: 'Feedback', ratings: props.ratings.filter((rating) => FEEDBACK_RATINGS.includes(rating)) },
        ].filter((group) => group.ratings.length > 0),
    )

    const emit = defineEmits(['update:modelValue'])

    function isDisabled(rating) {
        return props.disabled || props.disabledRatings.includes(rating)
    }

    function select(rating) {
        if (!isDisabled(rating)) {
            emit('update:modelValue', rating)
        }
    }
</script>

<template>
    <div
        role="radiogroup"
        aria-label="Rating"
        class="flex items-end gap-3"
    >
        <template
            v-for="(group, index) in groups"
            :key="group.label"
        >
            <span
                v-if="showGroupLabels && index > 0"
                data-test="rating-group-separator"
                class="w-px self-stretch bg-zinc-200"
                aria-hidden="true"
            />
            <div
                :data-test="`rating-group-${group.label.toLowerCase()}`"
                class="flex flex-col items-center gap-1"
            >
                <span
                    v-if="showGroupLabels"
                    data-test="rating-group-label"
                    class="text-[0.625rem] font-semibold uppercase leading-none tracking-wide text-zinc-500"
                    aria-hidden="true"
                >
                    {{ group.label }}
                </span>
                <div class="flex items-center gap-2 p-1">
                    <button
                        v-for="rating in group.ratings"
                        :key="rating"
                        type="button"
                        role="radio"
                        :data-test="`rating-${rating}`"
                        :aria-checked="modelValue === rating"
                        :aria-label="RATING_CIRCLE_LABELS[rating]"
                        :title="RATING_CIRCLE_LABELS[rating]"
                        :disabled="isDisabled(rating)"
                        class="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-lifeworx-blue-400 disabled:cursor-not-allowed"
                        @click="select(rating)"
                    >
                        <span
                            data-test="rating-circle"
                            class="block size-6 rounded-full transition"
                            :class="[RATING_CIRCLE_BG_CLASSES[rating], ratingCircleOpacity(modelValue === rating, isDisabled(rating)), { [RATING_CIRCLE_RING_CLASSES[rating]]: modelValue === rating }]"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>
